// ============================================================
// pins - the flat overrides, read as what the table pinned by hand.
//
// A combatant's `overrides` lay flat values over what its sheet resolves
// to. A PIN is one such value that disagrees with the sheet: "Toughness
// bonus 6, though the sheet gives 5". Pins are listed (so they can be
// shown and reset) and REBASED when the sheet itself is edited.
//
// Two override keys are never pins: `name` (the combatant's name lives
// on the combatant) and `forceField` (the live field is combat state,
// and legacy writers copy it here).
//
// Pure. Every function returns new objects.
// ============================================================
import type { DefenderSheet } from '../character/types';
import { sameValue as same } from '../canonicalJson';
import { LOC_NAMES } from '../constants';
import { VEHICLE_FACING_LABELS } from '../vehicleFacing';
import { cachedDefenderSheet } from './projection';
import type { Combatant } from './types';

type Overrides = Partial<DefenderSheet>;
type Bag = Record<string, unknown>;

/** The section of the sheet a pin is shown under. */
export type SheetSectionId =
  'characteristics' | 'vitals' | 'armour' | 'defence' | 'movement' | 'traits' | 'talents' | 'skills' | 'stances';

/** One hand-pinned value. `leaf` names the field inside dodge, parry or armour. */
export interface Pin {
  key: keyof DefenderSheet;
  leaf?: string;
  /** The pinned value, and what the sheet alone would give. */
  value: unknown;
  sheetValue: unknown;
  label: string;
  section: SheetSectionId;
}

const NOT_PINS: ReadonlySet<string> = new Set(['name', 'forceField']);

/**
 * The overrides split in two: the keys that are carried as they are and
 * never read as pins, and the rest. The one place that knows which is which.
 */
export function splitCarried(overrides: Overrides): { carried: Overrides; pinned: Overrides } {
  const entries = Object.entries(overrides ?? {});
  return {
    carried: Object.fromEntries(entries.filter(([key]) => NOT_PINS.has(key))) as Overrides,
    pinned: Object.fromEntries(entries.filter(([key]) => !NOT_PINS.has(key))) as Overrides,
  };
}

/** What an armour key is called: a hit location, a vehicle's facing, or the key itself (an amorphous `mass`). */
export function armourKeyName(key: string): string {
  return LOC_NAMES[key] ?? (VEHICLE_FACING_LABELS as Record<string, string>)[key] ?? key;
}

/**
 * Overrides that are real (they change the defence, and are carried
 * across edits like any other) but are bookkeeping, not something the
 * table pinned: the parry weapon system's own figures. Never listed.
 */
const UNLISTED: ReadonlySet<string> = new Set(['parryBaseSkill', 'defenderWeapons']);

/** Override keys whose value is an object pinned LEAF by leaf. */
const LEAFED: ReadonlySet<string> = new Set(['dodge', 'parry', 'armour']);

/** What each pin is called and where it shows; `key.leaf` for a leaf. Unlisted keys are named after themselves. */
const PIN_TEXT: Record<string, { label: string; section: SheetSectionId }> = {
  toughnessBonus: { label: 'Toughness bonus', section: 'characteristics' },
  strengthBonus: { label: 'Strength bonus', section: 'characteristics' },
  willpowerBonus: { label: 'Willpower bonus', section: 'characteristics' },
  unnaturalToughness: { label: 'Unnatural Toughness', section: 'characteristics' },
  unnaturalStrength: { label: 'Unnatural Strength', section: 'characteristics' },
  unnaturalAgility: { label: 'Unnatural Agility', section: 'characteristics' },
  unnaturalWeaponSkill: { label: 'Unnatural Weapon Skill', section: 'characteristics' },
  unnaturalWillpower: { label: 'Unnatural Willpower', section: 'characteristics' },
  woundsMax: { label: 'Wounds max', section: 'vitals' },
  hordeMaxMagnitude: { label: 'Magnitude max', section: 'vitals' },
  isHorde: { label: 'Horde', section: 'vitals' },
  psyRating: { label: 'Psy rating', section: 'vitals' },
  fearRating: { label: 'Fear', section: 'traits' },
  armour: { label: 'Armour', section: 'armour' },
  'dodge.canDodge': { label: 'Can dodge', section: 'defence' },
  'dodge.agility': { label: 'Dodge Agility', section: 'defence' },
  'dodge.additionalDodges': { label: 'Extra dodges', section: 'defence' },
  'parry.canParry': { label: 'Can parry', section: 'defence' },
  'parry.weaponSkill': { label: 'Parry Weapon Skill', section: 'defence' },
  'parry.hasPowerField': { label: 'Power field', section: 'defence' },
  'parry.additionalParries': { label: 'Extra parries', section: 'defence' },
  'parry.canParryRanged': { label: 'Deflect Shot', section: 'defence' },
  hasDeflectShot: { label: 'Deflect Shot', section: 'defence' },
  allowFullDefenceChain: { label: 'Mixed dodge and parry', section: 'defence' },
  size: { label: 'Size', section: 'movement' },
  tokenDiameter: { label: 'Token size', section: 'movement' },
  vehicleKind: { label: 'Vehicle kind', section: 'armour' },
  figure: { label: 'Figure', section: 'movement' },
  bodyType: { label: 'Body', section: 'movement' },
  trueGrit: { label: 'True Grit', section: 'talents' },
  hardy: { label: 'Hardy', section: 'talents' },
  ironJaw: { label: 'Iron Jaw', section: 'talents' },
  ironJawToughness: { label: 'Iron Jaw Toughness', section: 'talents' },
  counterAttackAvailable: { label: 'Counter Attack', section: 'talents' },
  astartes: { label: 'Astartes', section: 'traits' },
  stuffOfNightmares: { label: 'Stuff of Nightmares', section: 'traits' },
  fromBeyond: { label: 'From Beyond', section: 'traits' },
  blind: { label: 'Blind', section: 'traits' },
  incorporeal: { label: 'Incorporeal', section: 'traits' },
  undying: { label: 'Undying', section: 'traits' },
  xenos: { label: 'Xenos', section: 'traits' },
  machineAP: { label: 'Machine', section: 'traits' },
  naturalArmour: { label: 'Natural Armour', section: 'traits' },
  daemonicProtection: { label: 'Daemonic', section: 'traits' },
  regeneration: { label: 'Regeneration', section: 'traits' },
};

/** "Off" said against a sheet that says nothing at all is not a disagreement. */
const agrees = (value: unknown, sheetValue: unknown) =>
  same(value, sheetValue) || (sheetValue === undefined && (value === false || value === 0));
const isBag = (v: unknown): v is Bag => !!v && typeof v === 'object' && !Array.isArray(v);

/** "armour.rightArm" reads "Armour: Right Arm" when the table has no entry of its own. */
function textFor(key: string, leaf?: string): { label: string; section: SheetSectionId } {
  const exact = PIN_TEXT[leaf ? `${key}.${leaf}` : key];
  if (exact) return exact;
  const parent = PIN_TEXT[key] ?? { label: key, section: 'traits' as const };
  if (!leaf) return parent;
  return { label: `${parent.label}: ${armourKeyName(leaf)}`, section: parent.section };
}

/** The pins inside one override value: one per disagreeing leaf, or the value whole. */
function pinsOf(key: string, value: unknown, sheetValue: unknown): Pin[] {
  const pin = (leaf: string | undefined, v: unknown, s: unknown): Pin => ({
    key: key as keyof DefenderSheet,
    ...(leaf ? { leaf } : {}),
    value: v,
    sheetValue: s,
    ...textFor(key, leaf),
  });
  if (!LEAFED.has(key) || !isBag(value) || !isBag(sheetValue)) {
    return agrees(value, sheetValue) ? [] : [pin(undefined, value, sheetValue)];
  }
  return Object.entries(value)
    .filter(([leaf, v]) => !agrees(v, sheetValue[leaf]))
    .map(([leaf, v]) => pin(leaf, v, sheetValue[leaf]));
}

/** Every value the table pinned over this combatant's sheet. */
export function listPins(c: Combatant): Pin[] {
  const resolved = cachedDefenderSheet(c.sheet) as unknown as Bag;
  return Object.entries(c.overrides ?? {})
    .filter(([key]) => !NOT_PINS.has(key) && !UNLISTED.has(key))
    .flatMap(([key, value]) => pinsOf(key, value, resolved[key]));
}

/** Which pin: an override key, and the leaf inside it for dodge, parry and armour. */
export interface PinRef {
  key: keyof DefenderSheet;
  leaf?: string;
}

/** The overrides with one pin let go: the key, or one leaf of it, falls back to the sheet. */
export function resetPin(c: Combatant, key: keyof DefenderSheet, leaf?: string): Overrides {
  const { [key]: pinned, ...rest } = c.overrides as Bag;
  if (!leaf || !isBag(pinned)) return rest as Overrides;
  const resolved = (cachedDefenderSheet(c.sheet) as unknown as Bag)[key] as Bag;
  const next = { ...pinned, [leaf]: resolved?.[leaf] };
  return (same(next, resolved) ? rest : { ...rest, [key]: next }) as Overrides;
}

/** A leafed override on the new sheet: the leaves still pinned laid over what the sheet now gives. */
function rebasedLeaves(value: Bag, was: Bag, now: Bag): Bag {
  const stillPinned = Object.entries(value).filter(([leaf, v]) => !same(v, was[leaf]) && same(was[leaf], now[leaf]));
  return { ...now, ...Object.fromEntries(stillPinned) };
}

/** One override key carried across a sheet edit; `undefined` when the pin is gone. */
function rebasedValue(key: string, value: unknown, was: unknown, now: unknown): unknown {
  if (NOT_PINS.has(key)) return value;
  if (LEAFED.has(key) && isBag(value) && isBag(was) && isBag(now)) {
    const next = rebasedLeaves(value, was, now);
    return same(next, now) ? undefined : next;
  }
  // The sheet's own answer moved: the edit wins, and the pin is let go.
  return same(was, now) && !same(value, now) ? value : undefined;
}

/**
 * The overrides after the SHEET was edited.
 *
 * A pin survives a sheet edit only where the edit left the sheet's own
 * answer alone. Where the edit changed what the sheet resolves to
 * (Toughness 40 to 60 moves the bonus from 4 to 6) the edit wins and the
 * pin is let go - otherwise editing the sheet under a pin would appear
 * to do nothing. Inside dodge, parry and armour this is decided leaf by
 * leaf, so a pin on one field no longer freezes its siblings.
 *
 * Returns the SAME object when nothing changed, so identity-keyed caches
 * and the write clock stay still.
 */
export function rebasePins(overrides: Overrides, was: DefenderSheet, now: DefenderSheet): Overrides {
  const before = was as unknown as Bag;
  const after = now as unknown as Bag;
  const kept = Object.entries(overrides ?? {})
    .map(([key, value]) => [key, rebasedValue(key, value, before[key], after[key])] as const)
    .filter(([, value]) => value !== undefined);
  const next = Object.fromEntries(kept) as Overrides;
  return same(next, overrides) ? overrides : next;
}
